import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CalendarClock } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { BookDemoDialog } from '../../../modules/signup/BookDemoDialog';
import { pickSignupLanguage } from '../../../modules/signup/signup-language';

/** What the page tells the demo page it is drawn over (see the API's private-banner.ts). */
export const BOOK_DEMO_MESSAGE_SOURCE = 'ardealy-book-demo';
export type BookDemoMessage = { source: typeof BOOK_DEMO_MESSAGE_SOURCE; type: 'ready' | 'sent' | 'close' };

/** The query a demo page opens the form with: the demo's slug, its language, what we know of the shop. */
export function readBookDemoQuery(search: string) {
    const q = new URLSearchParams(search);
    const text = (key: string, max: number) => (q.get(key) || '').replace(/\s+/g, ' ').trim().slice(0, max) || undefined;
    const slug = (q.get('demo') || '').trim().toLowerCase();
    return {
        demoSlug: /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(slug) ? slug : undefined,
        company: text('company', 120),
        website: text('website', 300),
    };
}

/**
 * "Book a demo" for a prospect, from their generated demo page.
 *
 * The demo's private-demo bar opens this page in a frame over the shop, with
 * no background of its own (see App), so what the prospect sees is the same
 * dialog the signup shows, over the page they were looking at. One form, one
 * set of fields, slots and checks; the request reaches our own pipeline with
 * the demo's slug, and the API ties it to the prospect the demo was made for.
 *
 * Framed, it tells the demo page when it is ready (to drop the page's own
 * dimming), when the request went, and when to take the frame away. Opened in
 * a tab of its own (a link opened with a middle click), closing it leaves a
 * button to open it again rather than an empty page.
 */
const BookDemoPage = () => {
    const { t, i18n } = useTranslation();
    const { search } = useLocation();
    const query = useMemo(() => readBookDemoQuery(search), [search]);
    const framed = typeof window !== 'undefined' && window.parent !== window;
    const [open, setOpen] = useState(true);

    const tell = (type: BookDemoMessage['type']) => {
        // Nothing in it is private: any page that frames us may know the form closed.
        if (framed) window.parent.postMessage({ source: BOOK_DEMO_MESSAGE_SOURCE, type } as BookDemoMessage, '*');
    };

    // The demo page's language, which it passes as ?lang= (see pickSignupLanguage).
    useEffect(() => {
        const lng = pickSignupLanguage(search, typeof navigator !== 'undefined' ? navigator.languages || [navigator.language] : []);
        if (lng !== i18n?.language) i18n?.changeLanguage?.(lng);
        tell('ready');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onOpenChange = (next: boolean) => {
        setOpen(next);
        if (!next) tell('close');
    };

    return (
        <>
            <BookDemoDialog
                open={open}
                onOpenChange={onOpenChange}
                prefill={{ company: query.company, website: query.website }}
                demoSlug={query.demoSlug}
                journey={{ source: { utm_source: 'demo-site', ...(query.demoSlug ? { utm_campaign: query.demoSlug } : {}) } }}
                onSent={() => tell('sent')}
            />
            {!open && !framed && (
                <div className="flex min-h-dvh items-center justify-center p-4">
                    <Button variant="outline" onClick={() => setOpen(true)} data-testid="book-demo-reopen">
                        <CalendarClock className="size-4" />
                        {t('SIGNUP.DEMO.TITLE')}
                    </Button>
                </div>
            )}
        </>
    );
};

export default BookDemoPage;
